import React from 'react';

import {useGate, useUnit} from 'effector-react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {
    Employee,
    EmployeeWithFullName,
    getUserStatus,
    Roles,
} from '@/entities/eployersControl/type';
import {userModel} from '@/entities/user/model';
import {UserRole} from '@/entities/user/types';
import {mapEnumToI18nOptions} from '@/shared/lib/utils/select';
import {Input} from '@/shared/ui/Input';
import {LineControlComponent} from '@/shared/ui/LineControlComponent';
import {Select} from '@/shared/ui/Select';
import {
    EmployeesGate,
    employeesModel,
    pageNumberChanged,
    setEditEmployeeModal,
    setSelectedEmployeeForEdit,
} from '@entities/eployersControl/model';
import {Control} from '@pages/EployersControl/Control';
import {GridFieldConfig} from '@shared/types/grid';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {StatusLabel} from '../ui/StatusLabel';

type EmployeeColumnId = keyof EmployeeWithFullName;

const config = ({
    t,
    user,
}: {
    t: TFunction;
    user: {isDirector: boolean; isChiefInspector: boolean; isOperator: boolean};
}): GridFieldConfig<EmployeeColumnId>[] => {
    const directorColumns: GridFieldConfig<EmployeeColumnId>[] = user.isDirector
        ? [
              {
                  id: 'phone',
                  header: t('page_employers_control.table.phone'),
                  isSortable: true,
                  filter: <Input />,
              },
          ]
        : [];
    const chiefInspectorColumns: GridFieldConfig<EmployeeColumnId>[] = user.isChiefInspector
        ? [
              {
                  id: 'taxAuthorityDepartment',
                  header: 'Department',
                  isSortable: true,
              },
          ]
        : [];
    const operatorColumns: GridFieldConfig<EmployeeColumnId>[] = user.isOperator
        ? [
              {
                  id: 'taxAuthorityDepartment',
                  header: 'Department',
                  isSortable: true,
              },
              {
                  id: 'role',
                  header: 'Role',
                  isSortable: true,
                  filter: <Select options={mapEnumToI18nOptions(Roles, 'roles')} isClearable />,
              },
              {
                  id: 'company',
                  header: 'Company',
                  isSortable: true,
                  filter: <Input />,
              },
          ]
        : [];

    return [
        {id: 'fullName', header: t('page_employers_control.table.full_name'), isSortable: false},
        {
            id: 'email',
            header: t('page_employers_control.table.email'),
            isSortable: true,
            filter: <Input />,
        },
        ...directorColumns,
        ...chiefInspectorColumns,
        ...operatorColumns,
        {
            id: 'userStatus',
            header: t('page_employers_control.table.status'),
            isSortable: false,
            width: 120,
        },
        {
            id: 'actions',
            header: t('page_employers_control.table.actions'),
            isSortable: false,
            width: 80,
        },
    ];
};

const dataMapper = ({
    raw,
    userRole,
    userId,
    t,
}: {
    raw?: Employee[];
    userRole?: UserRole;
    userId?: number;
    t: TFunction;
}) => {
    if (!raw?.length) return [];
    console.log('userId', userId);
    return raw.map((row: Employee) => {
        const userStatus = getUserStatus({
            isActive: row.isActive,
        });
        return {
            ...row,
            fullName: `${row.firstName} ${row.lastName}`,
            userStatus: (
                <StatusLabel status={userStatus}>
                    {t(`page_employers_control.edit_employee.status.${userStatus}`)}
                </StatusLabel>
            ),
            role: <>{t(`roles.${row.role}`)}</>,
            actions:
                userRole === Roles.OPERATOR ? (
                    row.role === Roles.CHIEF_INSPECTOR ? (
                        <LineControlComponent
                            row={row}
                            userControlModal={() => {
                                setSelectedEmployeeForEdit(row);
                                setEditEmployeeModal(true);
                            }}
                            userStatus={userStatus}
                        />
                    ) : (
                        <div />
                    )
                ) : userId !== row.id ? (
                    <LineControlComponent
                        row={row}
                        userControlModal={() => {
                            setSelectedEmployeeForEdit(row);
                            setEditEmployeeModal(true);
                        }}
                        userStatus={userStatus}
                    />
                ) : (
                    <div />
                ),
        };
    });
};

export const EmployersList = () => {
    const employees = useUnit(employeesModel);
    const user = useUnit(userModel);
    const {t} = useTranslation();
    useGate(EmployeesGate);

    const data = React.useMemo(
        () =>
            dataMapper({
                raw: employees.state.data?.data,
                userRole: user.state.role ?? undefined,
                userId: user.state.profile?.id,
                t,
            }),
        [t, employees.state.data],
    );
    const columns = React.useMemo(
        () =>
            mapTableColumns(
                config({
                    t,
                    user: {
                        isDirector: user.isDirector,
                        isChiefInspector: user.isChiefInspector,
                        isOperator: user.isOperator,
                    },
                }),
                employees.state.filters,
                employees.filtersChanged,
                employees.filtersReset,
                employees.state.sortings,
                employees.sortingsChanged,
            ),
        [employees.state.filters, employees.state.sortings, user, t],
    );

    return (
        <Table
            headerControls={<Control />}
            columns={columns}
            data={data}
            isLoading={employees.state.pending}
            pageCount={employees.state.pageCount}
            onPageChange={pageNumberChanged}
            pageIndex={employees.state.pageNumber}
            onPageSizeChange={employees.pageSizeChanged}
        />
    );
};
